import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';

export function useAdminAuth() {
  const { currentUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!currentUser) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const checkAdmin = async () => {
      try {
        // 1) Check Auth custom claims (set via Admin SDK setCustomUserClaims)
        const token = await currentUser.getIdTokenResult();
        if (cancelled) return;
        const claimsRole = token.claims?.role as string | undefined;
        const claimsAdmin = token.claims?.admin as boolean | undefined;
        const fromClaims =
          (typeof claimsRole === 'string' && claimsRole.toLowerCase() === 'admin') ||
          claimsAdmin === true;
        if (fromClaims) {
          setIsAdmin(true);
          if (!cancelled) setLoading(false);
          return;
        }

        // 2) Fallback: Firestore users/{uid}.role (support "role" or "Role" - Firestore is case-sensitive)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (cancelled) return;
        const data = userDoc.data();
        const role = data?.role ?? data?.Role;
        const isAdminRole =
          (typeof role === 'string' && role.toLowerCase() === 'admin') ||
          (Array.isArray(role) && role.some((r) => String(r).toLowerCase() === 'admin'));
        setIsAdmin(!!isAdminRole);
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
      if (!cancelled) setLoading(false);
    };

    checkAdmin();
    return () => {
      cancelled = true;
    };
  }, [currentUser, currentUser?.uid, authLoading]);

  return { user: currentUser, isAdmin, loading };
}
